import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import apiClient from "@/lib/apiClient";

import {
  ArrowLeft,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
  Activity,
  ChevronRight,
  FileText,
  TrendingUp,
  TrendingDown,
  Minus,
  Search,
  Filter,
  Download,
  Pill,
  Shield,
  BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function PatientHistory() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");


  useEffect(() => {
    fetchPatientHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const fetchPatientHistory = async () => {
    try {
      setLoading(true);
      // Fetch all analyses and filter by patient if patientId is provided
      const response = await apiClient.get(`${API_BASE}/api/analyses`);
      let data = response.data || [];
      
      // If patientId is provided, filter analyses (in a real app, this would be server-side)
      if (patientId && patientId !== 'all') {
        data = data.filter(a => a.patient_data?.id === patientId || a.id.includes(patientId));
      }
      
      setAnalyses(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching patient history:", err);
      setError("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort analyses
  const filteredAnalyses = analyses
    .filter(analysis => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const patientAge = analysis.patient_data?.age?.toString() || "";
        const patientSex = analysis.patient_data?.sex === "M" ? "homme" : "femme";
        const meds = (analysis.medications_with_atc || []).map(m => m.name?.toLowerCase()).join(" ");
        
        if (!patientAge.includes(searchLower) && 
            !patientSex.includes(searchLower) &&
            !meds.includes(searchLower) &&
            !analysis.id.toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      
      // Status filter
      if (filterStatus === "fragile" && !analysis.fragility_assessment?.is_fragile) {
        return false;
      }
      if (filterStatus === "robuste" && analysis.fragility_assessment?.is_fragile) {
        return false;
      }
      if (filterStatus === "alerts" && (!analysis.stopp_alerts || analysis.stopp_alerts.length === 0)) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      
      if (sortOrder === "newest") {
        return dateB - dateA;
      } else if (sortOrder === "oldest") {
        return dateA - dateB;
      } else if (sortOrder === "risk_high") {
        return (b.risk_score || 0) - (a.risk_score || 0);
      } else if (sortOrder === "risk_low") {
        return (a.risk_score || 0) - (b.risk_score || 0);
      }
      return 0;
    });

  const getRiskColor = (score) => {
    if (score >= 70) return "bg-red-100 text-red-700 border-red-200";
    if (score >= 40) return "bg-amber-100 text-amber-700 border-amber-200";
    return "bg-emerald-100 text-emerald-700 border-emerald-200";
  };

  const getRiskTrend = (currentAnalysis, index) => {
    if (index >= filteredAnalyses.length - 1) return null;
    const previousAnalysis = filteredAnalyses[index + 1];
    const diff = currentAnalysis.risk_score - previousAnalysis.risk_score;
    
    if (diff > 5) return { icon: TrendingUp, color: "text-red-500", label: `+${diff}` };
    if (diff < -5) return { icon: TrendingDown, color: "text-emerald-500", label: `${diff}` };
    return { icon: Minus, color: "text-slate-400", label: "=" };
  };

  // Calculate statistics
  const stats = {
    total: filteredAnalyses.length,
    fragile: filteredAnalyses.filter(a => a.fragility_assessment?.is_fragile).length,
    avgRisk: filteredAnalyses.length > 0 
      ? Math.round(filteredAnalyses.reduce((sum, a) => sum + (a.risk_score || 0), 0) / filteredAnalyses.length)
      : 0,
    totalAlerts: filteredAnalyses.reduce((sum, a) => sum + (a.stopp_alerts?.length || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement de l'historique...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6" data-testid="patient-history-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/")}
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
            Historique des Analyses
          </h1>
          <p className="text-slate-500 mt-1">
            {filteredAnalyses.length} analyse(s) trouvée(s)
          </p>
        </div>

        <Button
          onClick={() => navigate("/new-analysis")}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="new-analysis-btn"
        >
          <FileText className="h-4 w-4 mr-2" />
          Nouvelle analyse
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-slate-200" data-testid="stat-total">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase">Total</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-sky-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200" data-testid="stat-fragile">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase">Fragiles</p>
                <p className="text-2xl font-bold text-rose-600">{stats.fragile}</p>
              </div>
              <Shield className="h-8 w-8 text-rose-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200" data-testid="stat-risk">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase">Risque moy.</p>
                <p className="text-2xl font-bold text-amber-600">{stats.avgRisk}/100</p>
              </div>
              <Activity className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200" data-testid="stat-alerts">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase">Notifications STOPP</p>
                <p className="text-2xl font-bold text-red-600">{stats.totalAlerts}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border border-slate-200">
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher par âge, sexe, médicament..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="filter-status">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrer par..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="fragile">Patients fragiles</SelectItem>
                <SelectItem value="robuste">Patients robustes</SelectItem>
                <SelectItem value="alerts">Avec notifications</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="sort-order">
                <SelectValue placeholder="Trier par..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Plus récent</SelectItem>
                <SelectItem value="oldest">Plus ancien</SelectItem>
                <SelectItem value="risk_high">Risque élevé</SelectItem>
                <SelectItem value="risk_low">Risque faible</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="border border-red-200 bg-red-50">
          <CardContent className="py-6 text-center">
            <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <p className="text-red-700">{error}</p>
            <Button 
              variant="outline" 
              onClick={fetchPatientHistory}
              className="mt-4"
            >
              Réessayer
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!error && filteredAnalyses.length === 0 && (
        <Card className="border-2 border-dashed border-slate-200">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FileText className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Aucune analyse trouvée
            </h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              {searchTerm || filterStatus !== "all" 
                ? "Aucune analyse ne correspond à vos critères de recherche."
                : "Commencez par créer une nouvelle analyse pour voir l'historique."}
            </p>
            <Button 
              onClick={() => navigate("/new-analysis")}
              className="bg-slate-900 hover:bg-slate-800"
            >
              Créer une analyse
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Analyses List */}
      {!error && filteredAnalyses.length > 0 && (
        <div className="space-y-3">
          {filteredAnalyses.map((analysis, index) => {
            const trend = getRiskTrend(analysis, index);
            const isFragile = analysis.fragility_assessment?.is_fragile;
            const patient = analysis.patient_data || {};
            const medsCount = analysis.medications_with_atc?.length || 0;
            const stoppCount = analysis.stopp_alerts?.length || 0;
            const startCount = analysis.start_recommendations?.length || 0;

            return (
              <Card 
                key={analysis.id}
                className="border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/analysis/${analysis.id}`)}
                data-testid={`analysis-card-${analysis.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Date & ID */}
                    <div className="flex items-center gap-3 md:w-48">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {new Date(analysis.created_at).toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "short",
                            year: "numeric"
                          })}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(analysis.created_at).toLocaleTimeString("fr-FR", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </p>
                      </div>
                    </div>

                    <Separator orientation="vertical" className="hidden md:block h-12" />

                    {/* Patient Info */}
                    <div className="flex items-center gap-3 md:w-40">
                      <User className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-900">
                          {patient.age ? `${patient.age} ans` : "N/A"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {patient.sex === "M" ? "Homme" : patient.sex === "F" ? "Femme" : "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Fragility Badge */}
                    <div className="md:w-32">
                      {isFragile ? (
                        <Badge className="bg-rose-100 text-rose-700 border border-rose-200">
                          <Shield className="h-3 w-3 mr-1" />
                          FRAGILE
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          ROBUSTE
                        </Badge>
                      )}
                    </div>

                    {/* Metrics */}
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex items-center gap-2">
                        <Pill className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-600">{medsCount} méd.</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-400" />
                        <span className="text-sm text-slate-600">{stoppCount} STOPP</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-emerald-400" />
                        <span className="text-sm text-slate-600">{startCount} START</span>
                      </div>
                    </div>

                    {/* Risk Score */}
                    <div className="flex items-center gap-3">
                      <div className={`px-4 py-2 rounded-lg font-bold text-sm ${getRiskColor(analysis.risk_score || 0)}`}>
                        {analysis.risk_score || 0}/100
                      </div>
                      
                      {trend && (
                        <div className={`flex items-center gap-1 ${trend.color}`}>
                          <trend.icon className="h-4 w-4" />
                          <span className="text-xs font-medium">{trend.label}</span>
                        </div>
                      )}
                      
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
